import * as R from 'ramda';
import { rules, rules2 } from '@sis-cc/dotstatsuite-components';
import { getDataflow, getDisplay } from './router';
import {
  getData,
  getDataflowName,
  getHCodes,
  getParsedAttributes,
  getCombinationsDefinition,
} from './sdmx';
import { getDimensionId as getMicrodataDimensionId } from './microdata';
import { getCustomAttributes } from './index';
import {
  getSeriesAdvancedAttributes as metadataLib_getSeriesAdvancedAttributes,
  getHeaderAdvancedAttributes as metadataLib_getHeaderAdvancedAttributes,
} from '../lib/sdmx/metadata';
import memoize from 'memoize-one';

// --- Simple Data Extraction Selectors ---

export const getMetadataCoordinates = memoize((state) => {
  const data = getData(state);
  return rules2.getMetadataCoordinates({ data });
});

export const getDataAttributes = memoize((state) =>
  R.pathOr([], ['structure', 'attributes', 'observation'], getData(state)),
);

export const getDataDimensions = memoize((state) =>
  R.pathOr([], ['structure', 'dimensions', 'observation'], getData(state)),
);

export const getDataObservations = memoize((state) =>
  R.pathOr({}, ['dataSets', 0, 'observations'], getData(state)),
);

// --- Calculated Selectors ---

export const getOneValueDimensions = memoize((state) => {
  const dimensions = getDataDimensions(state);
  const attributes = getParsedAttributes(state);
  return rules2.getOneValueDimensions(dimensions, attributes);
});

export const getRejectedDimensions = memoize((state) => {
  const dimensions = getDataDimensions(state);
  return R.filter(R.prop('rejected'), dimensions);
});

export const getHeaderCoordinates = memoize((state) => {
  const oneValueDimensions = getOneValueDimensions(state);
  return rules2.getHeaderCoordinates(oneValueDimensions);
});

const getParsedCombinations = memoize((state) => {
  const combinations = getCombinationsDefinition(state);
  const parsedAttributes = getParsedAttributes(state);
  const dimensions = getDataDimensions(state);
  return rules2.parseCombinations(combinations, parsedAttributes, dimensions);
});

export const getSeriesCombinations = memoize((state) => {
  const combinations = getParsedCombinations(state);
  const oneValueDimensions = getOneValueDimensions(state);
  return rules2.getSeriesCombinations(combinations, oneValueDimensions);
});

export const getRefinedAttributes = memoize((state) => {
  const parsedAttributes = getParsedAttributes(state);
  const seriesCombinations = getSeriesCombinations(state);
  return rules2.refineAttributes(parsedAttributes, seriesCombinations);
});

const getEnhancedObservations = memoize((state) => {
  const data = getData(state);
  const observations = rules.getObservations({ data });
  const attributes = getRefinedAttributes(state);
  const dimensions = getDataDimensions(state);
  const customAttributes = getCustomAttributes(state);
  return rules2.enhanceObservations(dimensions, observations, attributes, {
    customAttributes,
  });
});

export const getAttributesSeries = memoize((state) => {
  const observations = getEnhancedObservations(state);
  return rules2.getAttributesSeries(observations);
});

const getSeriesAdvancedAttributes = memoize((state) => {
  const attributesSeries = getAttributesSeries(state);
  const combinations = getParsedCombinations(state);
  const customAttributes = getCustomAttributes(state);
  return metadataLib_getSeriesAdvancedAttributes(
    attributesSeries,
    combinations,
    customAttributes,
  );
});

const getHeaderAdvancedAttributes = memoize((state) => {
  const attributes = getRefinedAttributes(state);
  const dimensions = getDataDimensions(state);
  const combinations = getParsedCombinations(state);
  const customAttributes = getCustomAttributes(state);
  return metadataLib_getHeaderAdvancedAttributes(
    attributes,
    dimensions,
    combinations,
    customAttributes,
  );
});

export const getAdvancedAttributes = memoize((state) => {
  const seriesAttrs = getSeriesAdvancedAttributes(state);
  const headerAttrs = getHeaderAdvancedAttributes(state);
  return { ...seriesAttrs, ...headerAttrs };
});

const getManyValuesDimensions = memoize((state) => {
  const dimensions = getDataDimensions(state);
  const attributesSeries = getAttributesSeries(state);
  const customAttributes = getCustomAttributes(state);
  const combinations = getSeriesCombinations(state);
  return rules2.getManyValuesDimensions(
    dimensions,
    attributesSeries,
    customAttributes,
    combinations,
  );
});

export const getHierarchisedDimensions = memoize((state) => {
  const dimensions = getManyValuesDimensions(state);
  const hierarchies = getHCodes(state);
  return R.map((dim) => {
    if (R.isEmpty(R.propOr({}, dim.id, hierarchies))) {
      return rules2.hierarchiseDimensionWithNativeHierarchy(dim);
    }
    return rules2.applyHierarchicalCodesToDim(R.prop(dim.id, hierarchies), dim);
  }, dimensions);
});

export const getObsAttributes = memoize((state) => {
  const attributes = getRefinedAttributes(state);
  return R.filter(
    (a) => a.series && R.isEmpty(a.relationship) && a.display && !a.combined,
    attributes,
  );
});

export const getDuplicatedObservations = memoize((state) => {
  const observations = getEnhancedObservations(state);
  const hierarchisedDimensions = getHierarchisedDimensions(state);
  return rules2.duplicateObs(R.values(hierarchisedDimensions), observations);
});

export const getHeaderCombinations = memoize((state) => {
  const combinations = getParsedCombinations(state);
  const dimensions = getOneValueDimensions(state);
  const attributes = getRefinedAttributes(state);
  const display = getDisplay(state);
  return rules2.getHeaderCombinations(
    combinations,
    dimensions,
    attributes,
    display,
  );
});

const getVisDataflow = memoize((state) => {
  const dataflow = getDataflow(state);
  const name = getDataflowName(state);
  return {
    id: dataflow.dataflowId,
    name,
  };
});

export const getDefaultTitleLabel = memoize((state) => {
  const dataflow = getVisDataflow(state);
  const display = getDisplay(state);
  return rules.dimensionValueDisplay(display)(dataflow);
});

const getDataflowAttributes = memoize((state) => {
  const attributes = getParsedAttributes(state);
  const combinations = getParsedCombinations(state);
  return rules2.getDataflowAttributes(attributes, combinations);
});

export const getHeaderTitle = memoize((state) => {
  const dataflow = getVisDataflow(state);
  const attributes = getDataflowAttributes(state);
  const display = getDisplay(state);
  const customAttributes = getCustomAttributes(state);
  return rules2.getHeaderTitle(dataflow, attributes, display, customAttributes);
});

export const getHeaderSubtitle = memoize((state) => {
  const dimensions = getOneValueDimensions(state);
  const combinations = getParsedCombinations(state);
  const customAttributes = getCustomAttributes(state);
  const display = getDisplay(state);
  const microdataDimension = getMicrodataDimensionId(state);
  return rules2.getHeaderSubtitle(
    dimensions,
    combinations,
    customAttributes,
    display,
    microdataDimension,
  );
});

export const getHeaderProps = memoize((state) => {
  const title = getHeaderTitle(state);
  const subtitle = getHeaderSubtitle(state);
  const combinations = getHeaderCombinations(state);
  return {
    title,
    subtitle,
    combinations,
  };
});
